import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocation } from 'react-router-dom';
import { useGlobalModal } from './ModalContext';
import { GameStartAccountOfferContext } from './GameStartAccountOfferContext';
import { useAds } from './AdsContext';
import { useResourceCelebration } from './ResourceCelebrationContext';
import { useAIHelper } from './AIHelperContext';
import { useAlertQueueState } from './AlertQueueContext';
import { GAME_CONFIG } from '../games';
import { presentGuestRewardOffer } from '../api/users';
import { storePendingRewardClaimTokens } from '../utils/rewardClaims';
import { guestRewardAuthMessage } from '../utils/guestRewardSummary';
import { buildAuthRedirectUrl, buildTrackedAuthRedirectUrl, trackAuthGateViewed, trackAuthPromptDismissed } from '../analytics';
import GameStartAccountOffer from '../components/GameStartAccountOffer';
import styles from '../styles/GameStartAccountOffer.module.css';

const SOURCE_SURFACE = 'guest_reward_offer';
const GATE_REASON = 'claim_guest_rewards';

export const GameStartAccountOfferProvider = ({ children }) => {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const { isAdBreakActive } = useAds() || {};
  const { flights = [] } = useResourceCelebration();
  const { hasPendingAlerts } = useAlertQueueState();
  const { isOpen: isAIHelperOpen } = useAIHelper();
  const { openModal, closeModal, isOpen, modalName } = useGlobalModal();
  const [pendingOffer, setPendingOffer] = useState(null);
  const [activeOffer, setActiveOffer] = useState(false);
  const [gamePresentationPending, setGamePresentationPending] = useState(false);
  const [isGameplayActive, setGameplayActive] = useState(false);
  const pendingRef = useRef(null);
  const activeRef = useRef(null);
  const seenSessionsRef = useRef(new Set());
  const locationRef = useRef(location);
  const modalRef = useRef({ isOpen, modalName, closeModal });
  const authRef = useRef({ isLoaded, isSignedIn });
  const busyRef = useRef(false);
  locationRef.current = location;
  modalRef.current = { isOpen, modalName, closeModal };
  authRef.current = { isLoaded, isSignedIn };
  busyRef.current = isOpen || isAIHelperOpen || isAdBreakActive || flights.length > 0 || hasPendingAlerts || gamePresentationPending;

  const scheduleOffer = useCallback((offer) => {
    if (authRef.current.isSignedIn) return;
    const current = locationRef.current;
    if (/^\/(admin|preview|signup|signin)(\/|$)/.test(current.pathname)) return;
    const pending = { ...offer, locationKey: current.key,
      nextPath: current.pathname + current.search + (current.hash || ''),
      readyAt: Date.now() + Math.max(0, Number(offer.delayMs) || 0) };
    pendingRef.current = pending;
    setPendingOffer(pending);
  }, []);

  const requestOffer = useCallback(({ sessionId, gameSlug, delayMs = 1000 }) => {
    const pathName = GAME_CONFIG[gameSlug]?.pathName;
    if (!sessionId || !pathName || !new RegExp(`^/${pathName}/\\d{4}-\\d{2}-\\d{2}/?$`).test(locationRef.current.pathname)) return;
    if (seenSessionsRef.current.has(sessionId)) return;
    seenSessionsRef.current.add(sessionId);
    scheduleOffer({ sessionId, gameSlug, trigger: 'game_completed', delayMs });
  }, [scheduleOffer]);

  const cancelOffer = useCallback((sessionId, reason = 'gameplay_action') => {
    if (pendingRef.current?.sessionId === sessionId) {
      pendingRef.current = null;
      setPendingOffer(null);
    }
    const active = activeRef.current;
    if (active?.sessionId === sessionId) {
      activeRef.current = null;
      setActiveOffer(false);
      if (modalRef.current.isOpen && modalRef.current.modalName === active.modalName) modalRef.current.closeModal(reason);
    }
  }, []);
  const cancelPendingOffer = useCallback(() => {
    // Completion offers wait for the results presentation, even after input.
  }, []);

  useEffect(() => {
    const offer = pendingRef.current || activeRef.current;
    if (!offer) return;
    const nextPath = location.pathname + location.search + (location.hash || '');
    if (isSignedIn || offer.nextPath !== nextPath || offer.locationKey !== location.key) {
      pendingRef.current = null;
      setPendingOffer(null);
      if (activeRef.current) closeModal(isSignedIn ? 'authenticated' : 'route_change');
    }
  }, [isSignedIn, location, closeModal]);

  useEffect(() => {
    if (!pendingOffer || !isLoaded || isSignedIn || isOpen || isAIHelperOpen || isAdBreakActive || flights.length || hasPendingAlerts || gamePresentationPending) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (pendingRef.current !== pendingOffer) return;
      try {
        const response = await presentGuestRewardOffer({
          trigger: pendingOffer.trigger,
          session_id: pendingOffer.sessionId || null,
        });
        if (cancelled || pendingRef.current !== pendingOffer || authRef.current.isSignedIn) return;
        if (!response?.allowed) {
          pendingRef.current = null;
          setPendingOffer(null);
          return;
        }
        pendingRef.current = null;
        setPendingOffer(null);
        if (busyRef.current) return;
        let isReturningDevice = false;
        try { isReturningDevice = localStorage.getItem('has-ever-authenticated') === 'true'; } catch (_) {}
        const claims = response.claims || [];
        const offerModalName = 'guest_reward_offer';
        const authPayload = (ctaKind) => ({ source_surface: SOURCE_SURFACE, gate_reason: GATE_REASON,
          cta_kind: ctaKind, next_path: pendingOffer.nextPath, is_returning_device: isReturningDevice,
          game_slug: pendingOffer.gameSlug,
          message: guestRewardAuthMessage(claims) });
        const authUrls = { sign_up: buildAuthRedirectUrl(authPayload('sign_up')), sign_in: buildAuthRedirectUrl(authPayload('sign_in')) };
        const authenticate = (ctaKind, event) => {
          storePendingRewardClaimTokens(response.tokens);
          buildTrackedAuthRedirectUrl(authPayload(ctaKind));
          if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) closeModal(`${ctaKind}_cta_click`);
        };
        activeRef.current = { ...pendingOffer, modalName: offerModalName };
        setActiveOffer(true);
        openModal({ title: 'Premi da riscattare', containerClassName: styles.modalContainer, alignToGame: true, trapFocus: true,
          content: <GameStartAccountOffer isReturningDevice={isReturningDevice} signupAmount={response.signup_amount}
            claims={claims} authUrls={authUrls} onAuthenticate={authenticate}
            onContinue={() => closeModal('continue_without_account')} />,
          onClose: (reason) => {
            activeRef.current = null;
            setActiveOffer(false);
            if (!['sign_up_cta_click', 'sign_in_cta_click', 'authenticated'].includes(reason)) {
              trackAuthPromptDismissed({ source_surface: SOURCE_SURFACE, gate_reason: GATE_REASON, reason });
            }
          },
          analytics: { source_surface: SOURCE_SURFACE, modal_name: offerModalName,
            trigger: pendingOffer.trigger, game_slug: pendingOffer.gameSlug } });
        trackAuthGateViewed({ ...authPayload(isReturningDevice ? 'sign_in' : 'sign_up'), message: undefined, trigger: pendingOffer.trigger });
      } catch (_) {
        if (pendingRef.current === pendingOffer) { pendingRef.current = null; setPendingOffer(null); }
      }
    }, Math.max(0, pendingOffer.readyAt - Date.now()));
    return () => { cancelled = true; clearTimeout(timer); };
  }, [pendingOffer, isLoaded, isSignedIn, isOpen, isAIHelperOpen, isAdBreakActive, flights.length, hasPendingAlerts, gamePresentationPending, openModal, closeModal]);

  const actions = useMemo(() => ({ requestOffer, cancelOffer, cancelPendingOffer, setGamePresentationPending,
    setGameplayActive, gamePresentationPending, isGameplayActive,
    hasPendingOffer: pendingOffer !== null || activeOffer,
  }), [requestOffer, cancelOffer, cancelPendingOffer, pendingOffer, activeOffer, gamePresentationPending, isGameplayActive]);
  return <GameStartAccountOfferContext.Provider value={actions}>{children}</GameStartAccountOfferContext.Provider>;
};
